import * as React from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import type { DatePickerLabels } from '@repo/ui-contracts';
import { cn } from '../lib/utils';

type DatePickerProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'value' | 'onChange' | 'min' | 'max'
> & {
  value: Date | null;
  onChange: (date: Date | null) => void;
  labels: Pick<DatePickerLabels, 'trigger'>;
  min?: Date;
  max?: Date;
};

function formatDateString(date: Date): string {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear());
  return `${day}-${month}-${year}`;
}

function buildDate(year: number, month: number, day: number): Date | null {
  const next = new Date(year, month - 1, day);
  if (
    next.getFullYear() !== year ||
    next.getMonth() !== month - 1 ||
    next.getDate() !== day
  ) {
    return null;
  }
  return next;
}

function parseDateString(value: string): Date | null {
  const trimmed = value.trim();
  const dayFirstMatch = /^(\d{2})-(\d{2})-(\d{4})$/.exec(trimmed);
  if (dayFirstMatch) {
    const [, dayRaw, monthRaw, yearRaw] = dayFirstMatch;
    return buildDate(Number(yearRaw), Number(monthRaw), Number(dayRaw));
  }

  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (isoMatch) {
    const [, yearRaw, monthRaw, dayRaw] = isoMatch;
    return buildDate(Number(yearRaw), Number(monthRaw), Number(dayRaw));
  }

  return null;
}

function isOutsideBounds(date: Date, min?: Date, max?: Date): boolean {
  const compareValue = date.getTime();
  if (min) {
    const minValue = new Date(min.getFullYear(), min.getMonth(), min.getDate()).getTime();
    if (compareValue < minValue) {
      return true;
    }
  }
  if (max) {
    const maxValue = new Date(max.getFullYear(), max.getMonth(), max.getDate()).getTime();
    if (compareValue > maxValue) {
      return true;
    }
  }
  return false;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const DAY_HEADERS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function CalendarGrid({
  value,
  min,
  max,
  onSelect,
}: {
  value: Date | null;
  min?: Date;
  max?: Date;
  onSelect: (date: Date) => void;
}) {
  const [viewDate, setViewDate] = React.useState(() => {
    const base = value ?? new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const prevMonth = () => setViewDate(new Date(year, month - 1, 1));
  const nextMonth = () => setViewDate(new Date(year, month + 1, 1));

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="w-64">
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          onClick={prevMonth}
          className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <ChevronLeft size={16} />
        </button>
        <span className="type-body-sm font-medium text-foreground">
          {MONTH_NAMES[month]} {year}
        </span>
        <button
          type="button"
          onClick={nextMonth}
          className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-0.5 text-center type-helper">
        {DAY_HEADERS.map((d) => (
          <div key={d} className="py-1 font-medium text-muted-foreground">
            {d}
          </div>
        ))}
        {cells.map((day, i) => {
          if (day === null) {
            return <div key={`empty-${i}`} />;
          }
          const date = new Date(year, month, day);
          const disabled = isOutsideBounds(date, min, max);
          const selected = value ? isSameDay(date, value) : false;
          const today = isSameDay(date, new Date());

          return (
            <button
              key={day}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(date)}
              className={cn(
                'h-control-sm w-control-sm rounded-md type-body-sm transition-colors',
                disabled && 'cursor-not-allowed text-muted-foreground/40',
                !disabled && !selected && 'hover:bg-muted',
                !disabled && !selected && today && 'font-semibold text-primary',
                selected && 'bg-primary text-primary-foreground',
              )}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const DatePicker = React.forwardRef<HTMLInputElement, DatePickerProps>(
  ({ value, onChange, labels, min, max, className, onBlur, ...props }, ref) => {
    const [draftValue, setDraftValue] = React.useState(value ? formatDateString(value) : '');
    const [open, setOpen] = React.useState(false);

    React.useEffect(() => {
      const nextDraft = value ? formatDateString(value) : '';
      setDraftValue((current) => (current === nextDraft ? current : nextDraft));
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const nextDraft = e.target.value;
      setDraftValue(nextDraft);

      if (!nextDraft.trim()) {
        onChange(null);
        return;
      }

      const parsed = parseDateString(nextDraft);
      if (!parsed || isOutsideBounds(parsed, min, max)) {
        return;
      }

      onChange(parsed);
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      setDraftValue(value ? formatDateString(value) : '');
      onBlur?.(event);
    };

    const handleCalendarSelect = (date: Date) => {
      onChange(date);
      setOpen(false);
    };

    return (
      <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
        <PopoverPrimitive.Anchor asChild>
          <div className="relative">
            <input
              ref={ref}
              type="text"
              inputMode="numeric"
              placeholder="dd-mm-yyyy"
              pattern="\d{2}-\d{2}-\d{4}"
              autoComplete="off"
              value={draftValue}
              onChange={handleChange}
              onBlur={handleBlur}
              aria-label={labels.trigger}
              className={cn(
                'h-control-md w-full rounded-md border border-input bg-background px-control-x-sm pr-10 type-body-sm text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/25 disabled:cursor-not-allowed disabled:opacity-disabled',
                className,
              )}
              {...props}
            />
            <PopoverPrimitive.Trigger asChild>
              <button
                type="button"
                tabIndex={-1}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-1 text-muted-foreground transition-colors hover:text-foreground"
                aria-label="Open calendar"
              >
                <CalendarIcon size={16} />
              </button>
            </PopoverPrimitive.Trigger>
          </div>
        </PopoverPrimitive.Anchor>
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            side="bottom"
            sideOffset={6}
            align="start"
            className="z-50 rounded-lg border border-border bg-popover p-3 shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          >
            <CalendarGrid
              value={value}
              min={min}
              max={max}
              onSelect={handleCalendarSelect}
            />
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    );
  },
);

DatePicker.displayName = 'DatePicker';

export { DatePicker };
export type { DatePickerProps };
